import React, { useEffect, useState } from "react";
import { ActionButton, classnames, Icon } from "@culturehq/components";

import { ProfileLink } from "app/links";
import FacebookLink from "app/links/FacebookLink";
import InstagramLink from "app/links/InstagramLink";
import TiktokLink from "app/links/TiktokLink";
import { Trans, getUniqueUsers } from "app/utils";

import SocialShareOptions from "components/admin/settings/SocialShareOptions";
import EngageLinkedin from "components/linkedin/EngageLinkedin";

const minuteDiff = (start, end) => (
  Math.floor((new Date(end) - new Date(start)) / 1000 / 60)
);

const getYesterdayDate = () => {
  const date = new Date();
  return date.setTime(date.getTime() - (24 * 60 * 60 * 1000));
};

const TrendingRSVPs = ({ event: { rsvpBreakdown: { accepted },
  rsvpPreviewTrending, rsvpPreview } }) => {
  let lastRSVPDatetime;
  const users = rsvpPreviewTrending?.map((rsvp, index) => {
    if (index === 0) {
      lastRSVPDatetime = new Date(rsvp.updatedAt);
    }
    return rsvp.user;
  });

  const [isToday] = useState(
    (lastRSVPDatetime && (lastRSVPDatetime.getTime() > getYesterdayDate()))
  );

  if (users) {
    return (
      <div className="FeedItem--header">
        {isToday && (<span role="img" aria-label="Fire">🔥</span>)} <ProfileLink user={users[0]} />
        {users[1] ? (
          <>
            , <ProfileLink user={users[1]} />,{" "}
            <Trans>and {accepted - 2} others are going to this event</Trans>
          </>
        ) : <>{" "}<Trans>is going to this event</Trans></>}
      </div>
    );
  }

  const usersPreview = rsvpPreview.map(({ user }) => user);

  return (
    <div className="FeedItem--header">
      <ProfileLink user={usersPreview[0]} />
      {usersPreview[1] ? (
        <>
          , <ProfileLink user={usersPreview[1]} />,{" "}
          <Trans>and {accepted - 2} others are going to this event</Trans>
        </>
      ) : <>{" "}<Trans>is going to this event</Trans></>}
    </div>
  );
};

const TrendingPost = ({ userActivity }) => {
  const usersObj = {};
  let interacted_with = "";
  let lastCommentDatetime;
  let lastCheerDatetime;

  const usersIds = getUniqueUsers(userActivity);

  userActivity.comments.forEach((comment, index) => {
    if (index === 0) {
      lastCommentDatetime = new Date(comment.createdAt);
    }
    usersObj[comment.user.id] = comment.user;
    interacted_with = "commented on";
  });

  userActivity.cheers.forEach((cheer, index) => {
    if (index === 0) {
      lastCheerDatetime = new Date(cheer.createdAt);
    }
    usersObj[cheer.user.id] = cheer.user;
    interacted_with = interacted_with === "commented on" || interacted_with === "interacted with"
      ? "interacted with"
      : "cheered";
  });

  const [users] = useState(Object.values(usersObj));
  const [isToday] = useState(
    (lastCommentDatetime && (lastCommentDatetime.getTime() > getYesterdayDate()))
     || (lastCheerDatetime && (lastCheerDatetime.getTime() > getYesterdayDate()))
  );
  const others = users.length === 3 ? "other" : "others";

  if (users.length > 0) {
    return (
      <div className="FeedItem--header">
        <span>
          {isToday && (<span role="img" aria-label="Fire">🔥</span>)} <ProfileLink user={users[0]} />
          {users[1] ? (
            <>
              { users.length === 2 ? (<> and</>) : (<>,</>)} <ProfileLink user={users[1]} />
              {users[2] ? (
                <>
                  ,{" "} <Trans>and {users.length - 2} {others} {interacted_with} this post</Trans>
                </>
              ) : <>{" "}<Trans>{interacted_with} this post</Trans></>}
            </>
          ) : <>{" "}<Trans>{interacted_with} this post</Trans></>}
        </span>
      </div>
    );
  }

  return (<></>);
};

const EngageAction = ({ socialShare, story }) => {
  if (socialShare.socialType === "facebook") {
    return (
      <FacebookLink facebookId={socialShare.externalId}>
        <ActionButton aria-label="Share" icon="thumbsup">
          <Trans>Engage</Trans>
        </ActionButton>
      </FacebookLink>
    );
  }

  if (socialShare.socialType === "instagram") {
    return (
      <InstagramLink link={socialShare.url}>
        <ActionButton aria-label="Share" icon="heart">
          <Trans>Engage</Trans>
        </ActionButton>
      </InstagramLink>
    );
  }

  if (socialShare.socialType === "linkedin") {
    return (
      <EngageLinkedin story={story} linkedinShare={socialShare} displayModalFromUrl />
    );
  }

  if (socialShare.socialType === "tiktok") {
    return (
      <TiktokLink link={socialShare.url}>
        <ActionButton aria-label="Share" icon="heart">
          <Trans>Engage</Trans>
        </ActionButton>
      </TiktokLink>
    );
  }

  return <></>;
};

const SocialPostHeader = ({ approvedForSocial, story, socialShare }) => {
  const [users] = useState(
    socialShare?.socialShareInteractions?.map(interaction => interaction?.creator)
    || (socialShare ? [socialShare?.creator] : undefined)
  );
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [others, setOthers] = useState("other");

  useEffect(
    () => {
      const filtered = users?.filter(
        (user, index, self) => index === self.findIndex(t => (t.id === user.id))
      );
      setFilteredUsers(filtered);
      setOthers(filtered?.length === 3 ? "other" : "others");
    },
    [users]
  );

  return (
    <div
      className={classnames(
        "FeedItem--header",
        "FeedItem--header_social",
        socialShare?.socialType ? `FeedItem--header_${socialShare?.socialType}` : ""
      )}
    >
      {socialShare ? (
        <>
          <span>
            <Trans>Join</Trans>
            {" "}
            <ProfileLink justFirstName user={filteredUsers[0]} />
            {" "}
            {filteredUsers[1] ? (
              <>
                {filteredUsers.length === 2
                  ? (<> and</>) : (<>,</>)} <ProfileLink justFirstName user={filteredUsers[1]} />
                {filteredUsers[2] ? (
                  <>
                    ,{" "} <Trans>and {users.length - 2} {others} and engage with this story</Trans>
                  </>
                ) : <>{" "}<Trans>and engage with this story</Trans></>}
              </>
            ) : <>{" "}<Trans>and engage with this story</Trans></>}
          </span>
          <EngageAction story={story} socialShare={socialShare} />
        </>
      ) : (
        <>
          <span><Trans>Be the first to share this on social!</Trans></span>
          <SocialShareOptions
            approvedForSocial={approvedForSocial}
            closeOnOutsideClick
            story={story}
          />
        </>
      )}
    </div>
  );
};

const UserActivityHeader = React.memo(({ userActivity }) => {
  const { pinned, activityType, bumpedAt, createdAt, entity } = userActivity;

  if (pinned) {
    return (
      <div className="FeedItem--header">
        <Icon icon="pin" /> Pinned
      </div>
    );
  }

  if (activityType === "event_created" && minuteDiff(createdAt, bumpedAt) >= 5) {
    return <TrendingRSVPs event={entity} />;
  }

  if (activityType === "post_created" && minuteDiff(createdAt, bumpedAt) >= 5) {
    return (
      <TrendingPost userActivity={userActivity} />
    );
  }

  if (activityType === "approved_for_social") {
    return (
      <SocialPostHeader
        approvedForSocial={userActivity.entity}
        story={userActivity.entity.entity}
      />
    );
  }

  if (activityType === "social_share") {
    return (
      <SocialPostHeader
        story={userActivity.entity.entity}
        socialShare={userActivity.entity}
      />
    );
  }

  if (activityType === "external_social_share") {
    return (
      <SocialPostHeader socialShare={userActivity.entity} />
    );
  }

  if (activityType === "story_created" && minuteDiff(createdAt, bumpedAt) >= 5) {
    return (
      <TrendingPost userActivity={userActivity} />
    );
  }

  return null;
});

export default UserActivityHeader;
